import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { InviteLinkService } from '../../global/invite-link/invite-link.service';
import { InviteLink } from '../../global/invite-link/models';
import { FeatureFlagService } from '../../global/feature-flag/feature-flag.service';
import { ToastService } from '../../global/toast/toast';

@Component({
    standalone: true,
    selector: 'abs-invite-link-admin',
    imports: [CommonModule, IonicModule, FormsModule],
    template: `
        <ion-header>
            <ion-toolbar>
                <ion-title>Invite Links</ion-title>
            </ion-toolbar>
        </ion-header>
        <ion-content class="ion-padding">
            <p>
                An invite link lets whoever opens it (once logged in) either grant themself a specific role, or be added to one
                feature's allow-list by email — the latter never touches roles at all, so a leaked link can only ever unlock the
                one feature it names. Toggle a link inactive to stop new redemptions instantly without affecting anyone who
                already redeemed it.
            </p>

            <ion-card class="new-invite">
                <ion-card-header>
                    <ion-card-title>Create Invite Link</ion-card-title>
                </ion-card-header>
                <ion-card-content>
                    <ion-segment [(ngModel)]="mode">
                        <ion-segment-button value="role">
                            <ion-label>Grant a role</ion-label>
                        </ion-segment-button>
                        <ion-segment-button value="feature">
                            <ion-label>Grant feature access</ion-label>
                        </ion-segment-button>
                    </ion-segment>

                    @if (mode() === 'role') {
                        <ion-item>
                            <ion-input
                                label="Role to grant"
                                labelPlacement="stacked"
                                placeholder="e.g. events_beta"
                                [(ngModel)]="newRole"
                            ></ion-input>
                        </ion-item>
                    } @else {
                        <ion-item>
                            <ion-select label="Feature to grant access to" labelPlacement="stacked" [(ngModel)]="newFeature">
                                @for (flag of featureFlagService.flags(); track flag.feature) {
                                    <ion-select-option [value]="flag.feature">{{ flag.feature }}</ion-select-option>
                                }
                            </ion-select>
                        </ion-item>
                    }

                    <ion-item>
                        <ion-input
                            label="Label (internal — shown to admins only, not the invitee)"
                            labelPlacement="stacked"
                            placeholder="e.g. Bristol Open — Events Beta"
                            [(ngModel)]="newLabel"
                        ></ion-input>
                    </ion-item>
                    <ion-item>
                        <ion-input
                            label="Title (shown to the invitee as the page heading)"
                            labelPlacement="stacked"
                            placeholder="e.g. You're invited to the Events beta"
                            [(ngModel)]="newTitle"
                        ></ion-input>
                    </ion-item>
                    <ion-item>
                        <ion-textarea
                            label="Description (shown to the invitee — basic HTML like <b>/<br>/<a> is allowed)"
                            labelPlacement="stacked"
                            placeholder="e.g. Try our new Events features early. <b>Thank you!</b>"
                            [(ngModel)]="newDescription"
                        ></ion-textarea>
                    </ion-item>
                    <ion-item>
                        <ion-input
                            label="Redirect to after accepting (optional path, e.g. /bolt-action-3e/events)"
                            labelPlacement="stacked"
                            placeholder="/bolt-action-3e/events"
                            [(ngModel)]="newRedirectUrl"
                        ></ion-input>
                    </ion-item>
                    <ion-button [disabled]="!canCreate()" (click)="create()">Create</ion-button>
                </ion-card-content>
            </ion-card>

            @if (justCreatedUrl(); as url) {
                <ion-card color="success">
                    <ion-card-content>
                        <p>Invite created. Share this link:</p>
                        <code>{{ url }}</code>
                        <ion-button fill="clear" size="small" (click)="copyToClipboard(url)">
                            <ion-icon name="copy-outline" slot="icon-only"></ion-icon>
                        </ion-button>
                    </ion-card-content>
                </ion-card>
            }

            <ion-list>
                @for (invite of invites(); track invite.code) {
                    <ion-card [color]="invite.active ? undefined : 'medium'">
                        <ion-card-header>
                            <ion-card-title class="invite-header">
                                <span>{{ invite.label || invite.role || invite.feature }}</span>
                                <ion-toggle [checked]="invite.active" (ionChange)="toggleActive(invite, $event)"></ion-toggle>
                                <ion-button fill="clear" color="danger" size="small" (click)="deleteInvite(invite)">
                                    <ion-icon name="trash-outline" slot="icon-only"></ion-icon>
                                </ion-button>
                            </ion-card-title>
                        </ion-card-header>
                        <ion-card-content>
                            @if (invite.role) {
                                <p>Grants role: <code>{{ invite.role }}</code></p>
                            } @else {
                                <p>Grants feature access: <code>{{ invite.feature }}</code></p>
                            }
                            @if (!invite.active) {
                                <p><em>Inactive — no longer accepts new redemptions.</em></p>
                            }
                            <p>Redeemed by {{ invite.redeemedByUserIds.length }} user(s)</p>
                            <code class="invite-url">{{ urlFor(invite) }}</code>
                            <ion-button fill="clear" size="small" (click)="copyToClipboard(urlFor(invite))">
                                <ion-icon name="copy-outline" slot="icon-only"></ion-icon>
                            </ion-button>
                        </ion-card-content>
                    </ion-card>
                }
            </ion-list>

            @if (invites().length === 0) {
                <p>No invite links yet.</p>
            }
        </ion-content>
    `,
    styles: [
        `
            .invite-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
            }
            .invite-url {
                display: block;
                word-break: break-all;
                margin-top: 4px;
            }
            code {
                background: var(--ion-color-light);
                padding: 1px 4px;
                border-radius: 3px;
                font-size: 13px;
            }
        `
    ]
})
export class InviteLinkAdminPage {
    private service = inject(InviteLinkService);
    private toastService = inject(ToastService);
    featureFlagService = inject(FeatureFlagService);

    invites = signal<InviteLink[]>([]);
    mode = signal<'role' | 'feature'>('role');
    newRole = '';
    newFeature = '';
    newLabel = '';
    newTitle = '';
    newDescription = '';
    newRedirectUrl = '';
    justCreatedUrl = signal<string | null>(null);

    constructor() {
        this.load();
    }

    private load() {
        this.service.getAll().subscribe((invites) => this.invites.set(invites));
    }

    urlFor(invite: InviteLink): string {
        // The admin's current browser origin is the real, currently-deployed web app URL
        // (dev/staging/production all differ), which is what a shareable link needs — there's
        // no separate "web base URL" config, only apiBaseUrl (which can point at a different
        // host/port in local dev).
        return `${window.location.origin}/invite/${invite.code}`;
    }

    canCreate(): boolean {
        return this.mode() === 'role' ? !!this.newRole.trim() : !!this.newFeature;
    }

    create() {
        if (!this.canCreate()) return;
        const data =
            this.mode() === 'role' ? { role: this.newRole.trim() } : { feature: this.newFeature };
        this.service
            .create({
                ...data,
                label: this.newLabel.trim() || undefined,
                title: this.newTitle.trim() || undefined,
                description: this.newDescription.trim() || undefined,
                redirectUrl: this.newRedirectUrl.trim() || undefined
            })
            .subscribe((invite) => {
                this.newRole = '';
                this.newFeature = '';
                this.newLabel = '';
                this.newTitle = '';
                this.newDescription = '';
                this.newRedirectUrl = '';
                this.justCreatedUrl.set(this.urlFor(invite));
                this.load();
            });
    }

    toggleActive(invite: InviteLink, event: any) {
        this.service.setActive(invite.code, event.detail.checked).subscribe(() => this.load());
    }

    deleteInvite(invite: InviteLink) {
        this.service.delete(invite.code).subscribe(() => this.load());
    }

    async copyToClipboard(value: string) {
        try {
            await navigator.clipboard.writeText(value);
            this.toastService.showToast('', 'Copied to clipboard', { color: 'success', duration: 2000 });
        } catch (err) {
            console.error(err);
            this.toastService.showToast('', 'Could not copy link', { color: 'danger', duration: 3000 });
        }
    }
}
